/**
 * ElevenLabs ConvAI Widget
 *
 * Renders the ElevenLabs conversational AI voice widget as a floating overlay.
 * Dynamically loads the widget script on mount to avoid blocking page load.
 *
 * Feature gated by: feature_eleven_labs_agent
 * When shown: Hides the Front chat launcher
 *
 * NOTE: The widget uses Shadow DOM with its own internal positioning.
 * The <elevenlabs-convai> element is designed to be a full-viewport overlay,
 * and internally positions the button at bottom-right using flexbox.
 * We can adjust the edge padding via the --el-overlay-padding CSS variable.
 *
 * Shadow DOM Style Injection:
 * Since the shadow root is "open", we can inject custom CSS to override internal styles.
 * This is used to hide the "Powered by ElevenLabs" branding when hideBranding is true.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { setLauncherVisible } from '@features/front-chat';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { useFeatureGate } from '@services/statsig';
import { useAppDispatch } from '@store/hooks';

const DEFAULT_ELEVENLABS_SCRIPT_SRC = 'https://unpkg.com/@elevenlabs/convai-widget-embed';
const DEFAULT_ELEVENLABS_AGENT_ID = 'agent_9001kd1hmtv6e24vx5se6fxc3snr';
const FEATURE_FLAG_NAME = 'feature_eleven_labs_agent';
const CUSTOM_STYLE_ID = 'jotpsych-elevenlabs-overrides';

interface ElevenLabsWidgetProps {
  agentId?: string;
  scriptSrc?: string;
  hideFrontChat?: boolean;
  /** Padding from viewport edges in pixels (default: 32, matches widget default) */
  edgePadding?: number;
  /** Hide the "Powered by ElevenLabs" branding (default: true) */
  hideBranding?: boolean;
  /** Scale factor for the widget (default: 1.0, use 0.8 for 80% size) */
  scale?: number;
  /** Show compact mode - hides "Need help?" text, just shows avatar + button (default: false) */
  compact?: boolean;
  /** Custom CSS to inject into the shadow DOM */
  customShadowCSS?: string;
  /** Position the widget on the left or right side of the screen (default: 'right') */
  position?: 'left' | 'right';
}

export const ElevenLabsWidget: React.FC<ElevenLabsWidgetProps> = ({
  agentId = DEFAULT_ELEVENLABS_AGENT_ID,
  scriptSrc = DEFAULT_ELEVENLABS_SCRIPT_SRC,
  hideFrontChat = true,
  edgePadding = 32,
  hideBranding = true,
  scale = 1.0,
  compact = false,
  customShadowCSS = '',
  position = 'right',
}) => {
  const isEnabled = useFeatureGate(FEATURE_FLAG_NAME);
  const dispatch = useAppDispatch();
  const scriptLoadedRef = useRef(false);
  const shadowStyleInjectedRef = useRef(false);

  // Persist hidden state in sessionStorage (per agent)
  const storageKey = `elevenlabs-widget-hidden-${agentId}`;
  const [isHidden, setIsHidden] = useState(() => {
    try {
      return sessionStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  });

  // Track if the widget is expanded (showing the full sheet)
  const [isExpanded, setIsExpanded] = useState(false);

  const handleHide = useCallback(() => {
    setIsHidden(true);
    try {
      sessionStorage.setItem(storageKey, 'true');
    } catch {
      // sessionStorage not available, state still works for current page
    }
  }, [storageKey]);

  // Hide Front chat when this widget is shown
  useEffect(() => {
    if (!isEnabled || !hideFrontChat) {
      return;
    }

    dispatch(setLauncherVisible(false));

    return () => {
      dispatch(setLauncherVisible(true));
    };
  }, [isEnabled, dispatch, hideFrontChat]);

  // Optionally adjust the widget's edge padding via CSS custom property
  useEffect(() => {
    if (!isEnabled || isHidden) {
      return;
    }

    const styleId = `elevenlabs-style-${agentId}`;
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement('style');
    style.id = styleId;

    // Override the widget's CSS custom property for edge padding
    // The widget uses --el-overlay-padding internally for positioning
    style.textContent = `
      elevenlabs-convai {
        --el-overlay-padding: ${edgePadding}px;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const styleToRemove = document.getElementById(styleId);
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, [isEnabled, isHidden, agentId, edgePadding]);

  // Build the CSS to inject into shadow DOM
  const buildShadowCSS = useCallback(() => {
    let css = '';

    // Determine transform origin based on position
    const transformOrigin = position === 'left' ? 'bottom left' : 'bottom right';

    // Position the widget on the left side if specified
    if (position === 'left') {
      css += `
        /* Position widget on the left side */
        .overlay {
          align-items: flex-start !important;
          /* Reduce bottom padding to lower the widget to align with Front chat */
          bottom: 20px !important;
        }
        
        /* Add extra left offset to collapsed widget ONLY to avoid overlapping with Front chat */
        /* Initial state uses rounded-sheet (without max-h), minimized uses rounded-compact-sheet */
        .rounded-sheet:not([class*="max-h-"]),
        .rounded-compact-sheet {
          margin-left: 70px !important;
        }
        
        /* The expanded chat panel (has max-h-[550px]) should NOT have the margin */
        .rounded-sheet[class*="max-h-"] {
          margin-left: 0 !important;
        }
      `;
    }

    // Hide the "Powered by ElevenLabs" branding
    if (hideBranding) {
      css += `
        /* Hide the "Powered by ElevenLabs" footer */
        .overlay p:has(a[href*="elevenlabs.io"]) {
          display: none !important;
        }
      `;
    }

    // Scale the widget content
    if (scale !== 1.0) {
      css += `
        /* Scale the collapsed button */
        .rounded-compact-sheet {
          transform: scale(${scale}) !important;
          transform-origin: ${transformOrigin} !important;
        }
        
        /* Scale the expanded chat panel */
        .rounded-sheet {
          transform: scale(${scale}) !important;
          transform-origin: ${transformOrigin} !important;
        }
      `;
    }

    // Compact mode - tighter spacing while keeping avatar and text
    // Note: Initial collapsed state may use "rounded-sheet", minimized-from-expanded uses "rounded-compact-sheet"
    // We need to target both, but NOT the expanded chat panel (which also uses rounded-sheet but has height constraints)
    if (compact) {
      css += `
        /* === COMPACT MODE: Hide avatar row, show only button === */
        
        /* Hide the entire first row (avatar + "Need help?" text) */
        /* This row is the first div.relative child of the rounded-sheet */
        .rounded-sheet:not([class*="max-h-"]) > div.relative.inline-flex:first-child,
        .rounded-compact-sheet > div.relative.inline-flex:first-child {
          display: none !important;
        }
        
        /* Make collapsed widget fit content */
        .rounded-sheet:not([class*="max-h-"]),
        .rounded-compact-sheet {
          padding: 8px !important;
          width: fit-content !important;
          max-width: fit-content !important;
        }
        
        /* Button wrapper should not grow */
        .rounded-sheet:not([class*="max-h-"]) .grow,
        .rounded-compact-sheet .grow {
          flex-grow: 0 !important;
        }
        
        /* Button should NOT be full width */
        .rounded-sheet:not([class*="max-h-"]) button.w-full,
        .rounded-compact-sheet button.w-full {
          width: auto !important;
        }
        
        /* Make button slightly smaller */
        .rounded-sheet:not([class*="max-h-"]) button.h-9,
        .rounded-compact-sheet button.h-9 {
          height: 34px !important;
          padding-left: 12px !important;
          padding-right: 12px !important;
        }
      `;
    }

    // Add any custom CSS
    if (customShadowCSS) {
      css += customShadowCSS;
    }

    return css;
  }, [hideBranding, scale, compact, customShadowCSS, position]);

  // Inject CSS into the widget's shadow DOM
  // Uses continuous monitoring to handle race conditions and re-renders
  useEffect(() => {
    if (!isEnabled || isHidden) {
      return;
    }

    const css = buildShadowCSS();
    if (!css) {
      return;
    }

    let shadowObserver: MutationObserver | null = null;
    let expandedPollInterval: number | null = null;

    // Function to inject styles into shadow root
    const injectShadowStyles = (shadowRoot: ShadowRoot): boolean => {
      // Check if our style already exists
      const existingStyle = shadowRoot.getElementById(CUSTOM_STYLE_ID);
      if (existingStyle) {
        return true;
      }

      // Create and inject new style - append LAST so our !important wins over widget's !important
      const style = document.createElement('style');
      style.id = CUSTOM_STYLE_ID;
      style.textContent = css;

      // Find all existing styles and append after them
      const existingStyles = shadowRoot.querySelectorAll('style');
      if (existingStyles.length > 0) {
        const lastStyle = existingStyles[existingStyles.length - 1];
        lastStyle.after(style);
      } else {
        shadowRoot.appendChild(style);
      }

      shadowStyleInjectedRef.current = true;
      return true;
    };

    // Check if widget is in an "active" state (expanded OR call active but minimized)
    // In either case, the X button should be positioned lower near the control bar
    const checkExpandedState = (shadowRoot: ShadowRoot) => {
      // The expanded sheet has class "rounded-sheet" with height constraints (h-[calc(100%-80px)])
      // The collapsed widget has class "rounded-compact-sheet"
      // When expanded, both exist in DOM but expanded sheet is visible

      // Look for the expanded sheet - it has max-h-[550px] which distinguishes it from collapsed
      const expandedSheet = shadowRoot.querySelector('.rounded-sheet.max-h-\\[550px\\]');
      const isExpandedSheetVisible =
        expandedSheet &&
        expandedSheet.getAttribute('data-shown') === 'true' &&
        getComputedStyle(expandedSheet).opacity !== '0';

      // Alternative: check if the expanded sheet's parent overlay is visible
      const overlay = shadowRoot.querySelector('.overlay[data-shown="true"]');
      const isOverlayVisible = overlay && !overlay.getAttribute('style')?.includes('display: none');

      // Check if call is active but minimized (has the end call button visible)
      const endCallButton = shadowRoot.querySelector('button[aria-label="End"]');
      const isCallActiveMinimized =
        endCallButton && getComputedStyle(endCallButton).display !== 'none';

      const expanded =
        !!isExpandedSheetVisible ||
        (!!isOverlayVisible && !!expandedSheet) ||
        !!isCallActiveMinimized;
      setIsExpanded(expanded);
    };

    // Function to set up shadow root observer
    const observeShadowRoot = (shadowRoot: ShadowRoot) => {
      // Inject immediately
      injectShadowStyles(shadowRoot);

      // Check initial expanded state
      checkExpandedState(shadowRoot);

      // Watch for changes inside shadow root (widget might re-render)
      shadowObserver = new MutationObserver(() => {
        // Re-inject if our style was removed
        if (!shadowRoot.getElementById(CUSTOM_STYLE_ID)) {
          injectShadowStyles(shadowRoot);
        }

        // Check if expanded state changed
        checkExpandedState(shadowRoot);
      });

      shadowObserver.observe(shadowRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'data-shown'],
      });

      // Also poll for expanded state changes (backup for MutationObserver)
      // This handles cases where style changes aren't caught by the observer
      expandedPollInterval = window.setInterval(() => {
        checkExpandedState(shadowRoot);
      }, 200);
    };

    // Function to find widget and set up observation
    const setupWidget = (): boolean => {
      const widget = document.querySelector(`elevenlabs-convai[agent-id="${agentId}"]`);
      if (widget?.shadowRoot) {
        observeShadowRoot(widget.shadowRoot);
        return true;
      }
      return false;
    };

    // Try to set up immediately
    setupWidget();

    // Poll to handle cases where shadow root isn't ready yet
    const pollInterval = setInterval(() => {
      if (!shadowObserver) {
        setupWidget();
      }
    }, 100);

    // Also observe document body for widget being added
    const bodyObserver = new MutationObserver(() => {
      if (!shadowObserver) {
        setupWidget();
      }
    });

    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Keep polling for longer to handle slow loads
    const timeout = setTimeout(() => {
      clearInterval(pollInterval);
    }, 30000);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(timeout);
      if (expandedPollInterval) clearInterval(expandedPollInterval);
      bodyObserver.disconnect();
      shadowObserver?.disconnect();
    };
  }, [isEnabled, isHidden, agentId, buildShadowCSS]);

  // Load ElevenLabs script
  useEffect(() => {
    if (!isEnabled || isHidden) {
      return;
    }

    // Avoid loading the script multiple times
    if (scriptLoadedRef.current) {
      return;
    }

    // Check if script already exists in DOM (check for both default and custom URLs)
    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);
    if (existingScript) {
      scriptLoadedRef.current = true;
      return;
    }

    // Create and append the script
    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;
    script.type = 'text/javascript';

    script.onload = () => {
      scriptLoadedRef.current = true;
    };

    script.onerror = () => {
      console.error('[ElevenLabsWidget] Failed to load script');
    };

    document.body.appendChild(script);
  }, [isEnabled, isHidden, scriptSrc]);

  if (!isEnabled || isHidden) {
    return null;
  }

  // Calculate X button position based on compact mode and expanded state
  // In compact mode, widget is just button (~50px height)
  // In non-compact mode, widget has avatar row too (~90px height)
  const getXButtonBottom = () => {
    const baseBottom = position === 'left' ? 20 : edgePadding;
    if (isExpanded) {
      // Near the minimized circle when call is active
      return `calc(${baseBottom}px + 50px)`;
    }
    // Above the compact widget (button-only is ~55px, full widget is ~90px)
    const widgetHeight = compact ? 50 : 90;
    return `calc(${baseBottom}px + ${widgetHeight}px)`;
  };

  return (
    <>
      <elevenlabs-convai agent-id={agentId} />
      {/* Close button - positioned to appear near the widget */}
      <IconButton
        onClick={handleHide}
        size="small"
        sx={{
          position: 'fixed',
          bottom: getXButtonBottom(),
          // Position on same side as widget
          // Add extra horizontal offset (70px) when on left side to avoid Front chat widget overlap
          ...(position === 'left'
            ? { left: `calc(${edgePadding}px + 70px)` }
            : { right: `${edgePadding}px` }),
          zIndex: 1001, // Above the widget (which uses z-index: 1000)
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          width: 24,
          height: 24,
          transition: 'bottom 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
          },
        }}
        aria-label="Hide voice assistant"
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </>
  );
};
